import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollText, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PaginationControls } from "../components/Pagination";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  suspend: { label: "Suspended User", color: "text-red-400", bg: "bg-red-500/15" },
  activate: { label: "Activated User", color: "text-emerald-400", bg: "bg-emerald-500/15" },
  "reset-password": { label: "Reset Password", color: "text-blue-400", bg: "bg-blue-500/15" },
  set_custom_rate: { label: "Set Custom Rate", color: "text-amber-400", bg: "bg-amber-500/15" },
  pause_billing: { label: "Paused Billing", color: "text-amber-400", bg: "bg-amber-500/15" },
  resume_billing: { label: "Resumed Billing", color: "text-green-400", bg: "bg-green-500/15" },
  update_notes: { label: "Updated Notes", color: "text-violet-400", bg: "bg-violet-500/15" },
  impersonate: { label: "Impersonated", color: "text-cyan-400", bg: "bg-cyan-500/15" },
  create_announcement: { label: "New Announcement", color: "text-blue-400", bg: "bg-blue-500/15" },
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 50;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-log?limit=5000`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setCurrentPage(1);
    } catch { toast.error("Failed to load audit log"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>Audit Log</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Track all admin actions across the platform ({total} total)</p>
        </div>
        <button onClick={() => fetchLogs()} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-1.5 transition-all self-start sm:self-auto" data-testid="refresh-audit-log">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /><span className="text-sm">Refresh</span>
        </button>
      </div>
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardContent className="p-0">
          <div className="divide-y divide-slate-800/30">
            {logs.length === 0 ? (
              <div className="text-center py-16 text-slate-500">No admin actions recorded yet</div>
            ) : logs.slice((currentPage - 1) * perPage, currentPage * perPage).map((l) => {
              const info = ACTION_LABELS[l.action] || { label: l.action, color: "text-slate-400", bg: "bg-slate-500/15" };
              return (
                <div key={l.id} className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-4 px-3 sm:px-5 py-3 hover:bg-slate-800/10 transition">
                  <div className="flex items-center gap-2 sm:gap-4">
                    <div className="text-[11px] sm:text-xs text-slate-600 w-auto sm:w-32 flex-shrink-0 font-mono">{formatDateTime(l.created_at)}</div>
                    <Badge className={`${info.bg} ${info.color} text-[10px] w-auto sm:w-36 justify-center`}>{info.label}</Badge>
                  </div>
                  <div className="flex-1 min-w-0 pl-0 sm:pl-0">
                    <span className="text-xs text-slate-400">by </span>
                    <span className="text-xs text-slate-200">{l.admin_name || l.admin_email}</span>
                    {l.target_id && (
                      <>
                        <span className="text-xs text-slate-600 mx-1.5">on</span>
                        <span className="text-xs text-slate-400 font-mono">{l.target_id.slice(0, 8)}...</span>
                      </>
                    )}
                    {l.details && <span className="text-xs text-slate-600 ml-2">({l.details})</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <PaginationControls currentPage={currentPage} totalPages={Math.max(1, Math.ceil(logs.length / perPage))} totalItems={logs.length} setCurrentPage={setCurrentPage} testIdPrefix="audit-pagination" />
    </div>
  );
}
